import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useCurrentProfilePhoto } from "../contexts/ProfileContext";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import ThemeWrapper from "../components/ThemeWrapper";
import NotificationBell from "../components/NotificationBell";
import BookingNotificationToast from "../components/BookingNotificationToast";
import GlobalNotificationMonitor from "../components/GlobalNotificationMonitor";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSpring, animated } from "@react-spring/web";
import { getTimeGreeting, formatCurrentTime } from "../utils/timeGreeting";
import notificationSound from "../utils/notificationSound";

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const profilePhoto = useCurrentProfilePhoto();
  const { isDark, toggleTheme } = useTheme();
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [currentTime, setCurrentTime] = useState(formatCurrentTime());
  const [timeGreeting, setTimeGreeting] = useState(getTimeGreeting());

  console.log('AdminLayout render - isDark:', isDark);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(formatCurrentTime());
      setTimeGreeting(getTimeGreeting());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const sidebarSpring = useSpring({
    transform: sidebarOpen ? 'translateX(0%)' : 'translateX(-100%)',
    config: { tension: 300, friction: 30 }
  });

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const confirmLogout = () => {
    setShowLogoutConfirm(true);
  };

  return (
    <div className={`flex min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={{ x: -256 }}
        animate={{ x: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className={`fixed inset-y-0 left-0 z-50 w-64 sm:w-72 ${isDark ? 'bg-gray-800/95 border-gray-700/30' : 'bg-white/95 border-white/30'} backdrop-blur-xl border-r shadow-2xl transform transition-all duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
      >
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`p-4 sm:p-6 border-b ${isDark ? 'border-gray-700/30' : 'border-white/30'}`}
        >
          <motion.div
            className="flex items-center"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400 }}
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", damping: 15, delay: 0.2 }}
              className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-3 shadow-md"
            >
              <img src="/logo.png" alt="Admin Lia" className="w-8 h-8 object-contain" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className={`font-bold text-base sm:text-lg ${isDark ? 'text-white' : 'text-black'}`}>Admin</div>
              <div className={`text-xs sm:text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Sewa Apartement by Lia</div>
            </motion.div>
          </motion.div>

        </motion.div>
        <motion.nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, staggerChildren: 0.1 }}
          className="flex flex-col p-3 sm:p-4 overflow-y-auto max-h-[calc(100vh-120px)]"
        >
          {/* Main Navigation */}
          <div className="mt-6">
            <motion.div
              className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider mb-4 px-4 flex items-center`}
              whileHover={{ scale: 1.05 }}
            >
              <motion.i
                className="fas fa-tasks mr-2"
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              ></motion.i>
              {t('management')}
            </motion.div>
            <motion.div
              className="space-y-2"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
              initial="hidden"
              animate="visible"
            >
              {[
                { to: "/admin/dashboard", icon: "fa-chart-line", label: t('dashboard') },
                { to: "/admin/users", icon: "fa-users", label: t('users') },
                { to: "/admin/properties", icon: "fa-building", label: t('properties') },
                { to: "/admin/gallery", icon: "fa-images", label: "Galeri" },
                { to: "/admin/discounts", icon: "fa-tags", label: "Diskon & Promo" },
                { to: "/admin/promo-settings", icon: "fa-bullhorn", label: "Pengaturan Promo" },
                { to: "/admin/promo-screen", icon: "fa-window-maximize", label: "Promo Screen" },
                { to: "/admin/bookings", icon: "fa-calendar-alt", label: t('bookings') },
                { to: "/admin/payments", icon: "fa-credit-card", label: t('payments') }
              ].map((item, index) => {
                const isActive = location.pathname === item.to;
                const label = item.to === "/admin/dashboard" ? t('dashboard') :
                  item.to === "/admin/users" ? t('users') :
                    item.to === "/admin/properties" ? t('properties') :
                      item.to === "/admin/gallery" ? "Galeri" :
                        item.to === "/admin/discounts" ? "Diskon & Promo" :
                          item.to === "/admin/bookings" ? t('bookings') :
                            item.to === "/admin/payments" ? t('payments') : item.label;
                return (
                  <motion.div
                    key={item.to}
                    variants={{
                      hidden: { opacity: 0, x: -20 },
                      visible: { opacity: 1, x: 0 }
                    }}
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      to={item.to}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center px-3 sm:px-4 py-2 sm:py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${isActive
                        ? `bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600'} text-white shadow-lg`
                        : `hover:bg-gradient-to-r ${isDark ? 'hover:from-gray-700 hover:to-gray-600 text-gray-300 hover:text-gray-100' : 'hover:from-blue-50 hover:to-blue-100 text-gray-600 hover:text-blue-600'}`
                        }`}
                    >
                      <motion.i
                        className={`fas ${item.icon} mr-2 sm:mr-3 text-base sm:text-lg`}
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      ></motion.i>
                      <span className="font-medium text-sm sm:text-base">{label}</span>
                      {isActive && (
                        <motion.div
                          layoutId="activeTab"
                          className={`absolute inset-0 bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600'} rounded-xl -z-10`}
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </div>

          {/* Analytics Section */}
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
          >
            <motion.div
              className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider mb-4 px-4 flex items-center`}
              whileHover={{ scale: 1.05 }}
            >
              <motion.i
                className={`fas fa-chart-pie mr-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              ></motion.i>
              {t('analytics')}
            </motion.div>
            <motion.div
              className="space-y-2"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
              initial="hidden"
              animate="visible"
            >
              {[
                { to: "/admin/analytics", icon: "fa-chart-bar", label: t('analytics') },
                { to: "/admin/reports", icon: "fa-file-alt", label: t('reports') },
                { to: "/admin/user-management", icon: "fa-user-cog", label: t('user_management') }
              ].map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <motion.div
                    key={item.to}
                    variants={{
                      hidden: { opacity: 0, x: -20 },
                      visible: { opacity: 1, x: 0 }
                    }}
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      to={item.to}
                      className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${isActive
                        ? `bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600'} text-white shadow-lg`
                        : `hover:bg-gradient-to-r ${isDark ? 'hover:from-gray-700 hover:to-gray-600 text-gray-300 hover:text-gray-100' : 'hover:from-blue-50 hover:to-blue-100 text-gray-600 hover:text-blue-600'}`
                        }`}
                    >
                      <motion.i
                        className={`fas ${item.icon} mr-3 text-lg`}
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      ></motion.i>
                      <span className="font-medium">{item.label}</span>
                      {isActive && (
                        <motion.div
                          layoutId="activeAnalyticsTab"
                          className={`absolute inset-0 bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600'} rounded-xl -z-10`}
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>

          {/* System Section */}
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
          >
            <motion.div
              className={`text-xs font-bold ${isDark ? 'text-gray-400' : 'text-gray-500'} uppercase tracking-wider mb-4 px-4 flex items-center`}
              whileHover={{ scale: 1.05 }}
            >
              <motion.i
                className={`fas fa-cogs mr-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}
                animate={{ rotate: [0, 360] }}
                transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
              ></motion.i>
              {t('system')}
            </motion.div>
            <motion.div
              className="space-y-2"
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
              initial="hidden"
              animate="visible"
            >
              {[
                { to: "/admin/activity-logs", icon: "fa-history", label: t('activity_logs') },
                { to: "/admin/settings", icon: "fa-cog", label: t('settings') }
              ].map((item) => {
                const isActive = location.pathname === item.to;
                return (
                  <motion.div
                    key={item.to}
                    variants={{
                      hidden: { opacity: 0, x: -20 },
                      visible: { opacity: 1, x: 0 }
                    }}
                    whileHover={{ scale: 1.02, x: 4 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Link
                      to={item.to}
                      className={`flex items-center px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${isActive
                        ? `bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600'} text-white shadow-lg`
                        : `hover:bg-gradient-to-r ${isDark ? 'hover:from-gray-700 hover:to-gray-600 text-gray-300 hover:text-gray-100' : 'hover:from-blue-50 hover:to-blue-100 text-gray-600 hover:text-blue-600'}`
                        }`}
                    >
                      <motion.i
                        className={`fas ${item.icon} mr-3 text-lg`}
                        whileHover={{ rotate: 360 }}
                        transition={{ duration: 0.5 }}
                      ></motion.i>
                      <span className="font-medium">{item.label}</span>
                      {isActive && (
                        <motion.div
                          layoutId="activeSystemTab"
                          className={`absolute inset-0 bg-gradient-to-r ${isDark ? 'from-gray-600 to-gray-700' : 'from-blue-500 to-blue-600'} rounded-xl -z-10`}
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          </motion.div>

          {/* Logout Button */}
          <motion.div
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
          >
            <motion.button
              onClick={confirmLogout}
              className={`flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 group relative overflow-hidden ${isDark
                ? 'hover:bg-gradient-to-r hover:from-red-900/20 hover:to-pink-900/20 text-gray-300 hover:text-red-400'
                : 'hover:bg-gradient-to-r hover:from-red-50 hover:to-pink-50 text-gray-600 hover:text-red-600'
                }`}
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.i
                className="fas fa-sign-out-alt mr-3 text-lg"
                whileHover={{ rotate: 180 }}
                transition={{ duration: 0.3 }}
              ></motion.i>
              <span className="font-medium">{t('logout')}</span>
            </motion.button>
          </motion.div>
        </motion.nav>
      </motion.aside>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 xl:ml-72 min-h-screen relative">
        {/* Background Image */}
        <div className="fixed inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920&h=1080&fit=crop&crop=center"
            alt="Building Background"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-br from-gray-900/95 via-gray-900/90 to-gray-900/95' : 'bg-gradient-to-br from-white/95 via-blue-50/90 to-blue-50/95'}`}></div>
        </div>

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`fixed top-0 left-0 right-0 lg:left-64 xl:left-72 z-40 ${isDark ? 'bg-gray-900/95 border-gray-800/30' : 'bg-white/95 border-white/30'} backdrop-blur-xl shadow-xl border-b px-3 sm:px-4 lg:px-6 py-3 sm:py-4`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <motion.button
                onClick={() => setSidebarOpen(true)}
                className={`lg:hidden p-1.5 sm:p-2 rounded-md ${isDark ? 'text-gray-300 hover:text-gray-100 hover:bg-gray-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'} mr-2 sm:mr-3`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <motion.svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  whileHover={{ rotate: 180 }}
                  transition={{ duration: 0.3 }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </motion.svg>
              </motion.button>
              <div className="flex items-center">
                <motion.h1
                  className={`text-lg sm:text-xl lg:text-2xl font-semibold ${isDark ? 'text-white' : 'text-black'} mr-2 sm:mr-4`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <span className="hidden sm:inline">{t('admin_panel')}</span>
                  <span className="sm:hidden">Admin</span>
                </motion.h1>
                <motion.button
                  onClick={() => {
                    console.log('Theme button clicked');
                    toggleTheme();
                  }}
                  className={`p-1.5 sm:p-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors mr-1 sm:mr-2`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <motion.i
                    className={`fas ${isDark ? 'fa-sun text-yellow-500' : 'fa-moon text-gray-600'} text-sm sm:text-lg`}
                    animate={{ rotate: isDark ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  />
                </motion.button>
                <motion.button
                  onClick={toggleLanguage}
                  className={`p-1.5 sm:p-2 rounded-lg ${isDark ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'} transition-colors flex items-center space-x-1`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.5 }}
                >
                  <span className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'} hidden sm:inline`}>{language.toUpperCase()}</span>
                  <i className={`fas fa-globe text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}></i>
                </motion.button>
              </div>
            </div>
            <motion.div
              className="flex items-center space-x-2 lg:space-x-4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <NotificationBell />
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.05 }}
              >
                <motion.div
                  className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg overflow-hidden"
                  whileHover={{ rotate: 360 }}
                  transition={{ duration: 0.5 }}
                >
                  {profilePhoto && profilePhoto !== 'https://ui-avatars.com/api/?name=Admin&background=3b82f6&color=fff' ? (
                    <img src={profilePhoto} alt="User" className="w-full h-full object-cover" onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://ui-avatars.com/api/?name=' + encodeURIComponent(user?.name || 'Admin') + '&background=3b82f6&color=fff';
                    }} />
                  ) : (
                    <span className="text-white text-xs sm:text-sm font-bold">{(user?.name || 'Admin').charAt(0).toUpperCase()}</span>
                  )}
                </motion.div>
                <span className={`hidden md:block text-xs sm:text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{user?.name || 'Admin'}</span>
              </motion.div>
            </motion.div>
          </div>
        </motion.header>

        {/* Content */}
        <motion.div
          className="relative p-3 sm:p-4 lg:p-6 pt-20 sm:pt-24 lg:pt-28"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <ThemeWrapper>
            <Outlet />
          </ThemeWrapper>
        </motion.div>
      </main>

      {/* Global Notification Monitor */}
      <GlobalNotificationMonitor />

      {/* Booking Notification Toast */}
      <BookingNotificationToast />

      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
            onClick={() => setShowLogoutConfirm(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className={`backdrop-blur-xl rounded-2xl shadow-2xl border p-6 max-w-md w-full ${isDark ? 'bg-gray-800/95 border-gray-700/20' : 'bg-white/95 border-white/20'}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: "spring", stiffness: 300 }}
                  className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <i className="fas fa-sign-out-alt text-2xl text-red-600"></i>
                </motion.div>
                <motion.h3
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}
                >
                  Konfirmasi Logout
                </motion.h3>
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className={`mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}
                >
                  Apakah Anda yakin ingin keluar dari sistem?
                </motion.p>
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="flex space-x-3"
                >
                  <motion.button
                    onClick={() => setShowLogoutConfirm(false)}
                    className={`flex-1 px-4 py-2 rounded-xl transition-colors ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Batal
                  </motion.button>
                  <motion.button
                    onClick={handleLogout}
                    className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-xl transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Ya, Logout
                  </motion.button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}